import { useRef, useState, useImperativeHandle, RefObject, ComponentProps } from 'react';
import { ELinxConnectionMethod, EmbedUrlRequest } from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import ElinxConnect, { EventMessage } from '@/components/ElinxConnect/ElinxConnect';

export type ElinxConnectRef = {
  open: ElinxConnectMethod;
};
type WrapperEmbedUrlRequest = Omit<
  EmbedUrlRequest,
  'RedirectURL' | 'RedirectMethod' | 'ClientAccountID' | 'ContactID' | 'PaymentSelectionMethod'
> & {
  PaymentSelectionMethod: ELinxConnectionMethod;
};
type ElinxConnectMethod = (values: WrapperEmbedUrlRequest, accountID: string) => Promise<string | undefined>;

type ElinxConnectWrapperProps = {
  ref: RefObject<ElinxConnectRef | null>;
};

export default function ElinxConnectWrapper({ ref }: ElinxConnectWrapperProps) {
  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();

  const [settings, setSettings] = useState<ComponentProps<typeof ElinxConnect>['setting'] | null>(null);

  const resolver = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }>(null);

  async function open(values: WrapperEmbedUrlRequest, accountID?: string): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      setSettings(buildSettings(values, accountID));
      resolver.current = { resolve, reject };
    });
  }

  useImperativeHandle(ref, () => {
    return {
      open,
    };
  });

  const onCloseElinxConnect = () => {
    setSettings(null);

    if (resolver.current) {
      resolver.current.reject(new Error('User cancelled ElinxConnect'));
      resolver.current = null;
    }
  };

  const onConnect = (event: EventMessage) => {
    const token = event.data.Token;
    setSettings(null);

    if (resolver.current) {
      resolver.current.resolve(token);
      resolver.current = null;
    }
  };

  function buildSettings(values: WrapperEmbedUrlRequest, accountID?: string) {
    return {
      Country,

      RedirectURL: 'https://vopay.com',

      RedirectMethod: 'JavascriptMessage' as const,
      ClientAccountID: clientAccountsEnabled ? accountID : '',
      ContactID: !clientAccountsEnabled ? accountID : '',
      ...values,
    };
  }

  if (!settings) return null;

  return <ElinxConnect setting={settings} onSuccess={onConnect} onClose={onCloseElinxConnect} />;
}
